import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { toast } from 'sonner';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import CareerFormations from '../components/CareerFormations';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, Medal, Award, RefreshCw, ArrowLeft, Star, Sparkles,
  Home, ExternalLink, Target, Users, Calendar, ChevronRight,
  Compass, Heart, Lightbulb, TrendingUp, PartyPopper, Rocket,
  Briefcase, GraduationCap, MapPin, Euro, CheckCircle, AlertCircle,
  MessageCircle, BookOpen, UserCheck, Search, Download, Image, FileText,
  Loader2, Share2, Linkedin, Facebook, Twitter, Link, Copy, Mail, Check
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Confetti animation component
const Confetti = () => {
  const colors = ['#A4187A', '#FB6823', '#FFD700', '#E91E63', '#4CAF50'];
  const particles = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    delay: Math.random() * 2,
    duration: 2 + Math.random() * 2,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: 4 + Math.random() * 6,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-50">
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: `${particle.x}%`,
            backgroundColor: particle.color,
            width: particle.size,
            height: particle.size,
          }}
          initial={{ y: -20, opacity: 1, rotate: 0 }}
          animate={{ 
            y: window.innerHeight + 20, 
            opacity: [1, 1, 0],
            rotate: 360 * (Math.random() > 0.5 ? 1 : -1),
            x: (Math.random() - 0.5) * 200
          }}
          transition={{ 
            duration: particle.duration, 
            delay: particle.delay,
            ease: "linear"
          }}
        />
      ))}
    </div>
  );
};

export default function Stage2Results() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const [showConfetti, setShowConfetti] = useState(true);
  const [celebrationComplete, setCelebrationComplete] = useState(false);
  const [careerMarketData, setCareerMarketData] = useState(null);
  const [showPostTest, setShowPostTest] = useState(false);
  const [postTestAnswer, setPostTestAnswer] = useState([]);
  const [engagementAnswer, setEngagementAnswer] = useState([]);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // 'pdf', 'image', or 'email'
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingImage, setDownloadingImage] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [email, setEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  
  // Support both naming conventions for backward compatibility
  const result = location.state?.stage2Result || location.state?.result;
  const selectedCareers = location.state?.selectedCareers || [];
  const originalResult = location.state?.originalResult;
  const sessionId = location.state?.sessionId;
  
  // Toggle selection for multi-select questions (max 2)
  const togglePostTestAnswer = (value) => {
    setPostTestAnswer(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else if (prev.length < 2) {
        return [...prev, value];
      }
      return prev; // Don't add if already 2 selected
    });
  };

  const toggleEngagementAnswer = (value) => {
    setEngagementAnswer(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else if (prev.length < 2) {
        return [...prev, value];
      }
      return prev; // Don't add if already 2 selected
    });
  };
  
  // Trigger confetti and celebration
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(false);
      setCelebrationComplete(true);
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  // Fetch labour market data for top career
  useEffect(() => {
    if (result?.top_career) {
      fetchCareerMarketData(result.top_career);
    }
  }, [result?.top_career]);

  const fetchCareerMarketData = async (careerName) => {
    try {
      const response = await axios.get(`${API}/labour-market/career/${encodeURIComponent(careerName)}?language=${language}`);
      setCareerMarketData(response.data);
    } catch (error) {
      console.error('Error fetching market data:', error);
    }
  };

  // Download PDF results (includes Stage 1 results)
  // Check feedback before action
  const handleActionWithFeedback = (action) => {
    if (feedbackSubmitted) {
      // Feedback already given, execute action directly
      if (action === 'pdf') executeDownloadPdf();
      else if (action === 'image') executeDownloadImage();
      else if (action === 'email') executeSendEmail();
    } else {
      // Show feedback modal first
      setPendingAction(action);
      setShowFeedbackModal(true);
    }
  };

  // Submit feedback and then execute pending action
  const handleFeedbackSubmitAndAction = async () => {
    // Submit feedback
    try {
      await axios.post(`${API}/analytics/post-test`, {
        session_id: sessionId,
        post_test_answer: postTestAnswer,
        engagement_answer: engagementAnswer,
        language
      });
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
    
    setFeedbackSubmitted(true);
    setShowFeedbackModal(false);
    
    // Execute the pending action
    if (pendingAction === 'pdf') executeDownloadPdf();
    else if (pendingAction === 'image') executeDownloadImage();
    else if (pendingAction === 'email') executeSendEmail();
    
    setPendingAction(null);
  };

  const executeDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const topDomains = getTopDomains();
      
      // Prepare Stage 1 data if available
      const stage1Scores = originalResult?.scores || null;
      const stage1Careers = originalResult?.careers ? 
        originalResult.careers.map(c => ({
          name: c.name || c.title,
          description: c.description || ''
        })) : null;
      
      const response = await axios.post(`${API}/export/stage2-pdf`, {
        ranked_careers: result.ranked_careers,
        top_career: result.top_career,
        explanation: result.explanation,
        top_domains: topDomains,
        language,
        // Include Stage 1 data for combined export
        stage1_scores: stage1Scores,
        stage1_careers: stage1Careers
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = language === 'fr' ? 'resultats-exploration-declic.pdf' : 'verkenningsresultaten-declic.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      const detail = error.response?.data?.detail || error.message || '';
      toast?.error?.(language === 'fr' 
        ? `Erreur lors du téléchargement PDF. ${detail}` 
        : `Fout bij downloaden PDF. ${detail}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadPdf = () => handleActionWithFeedback('pdf');

  // Download shareable image
  const executeDownloadImage = async () => {
    setDownloadingImage(true);
    try {
      // Use Stage 1 scores and careers for shareable image
      const response = await axios.post(`${API}/export/image`, {
        scores: originalResult?.scores || {},
        careers: result.ranked_careers.map(c => ({
          name: c.name,
          description: '',
          match: `${c.score}%`
        })),
        language
      }, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = language === 'fr' ? 'profil-declic.png' : 'declic-profiel.png';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading image:', error);
    } finally {
      setDownloadingImage(false);
    }
  };

  const handleDownloadImage = () => handleActionWithFeedback('image');

  // Social sharing functions
  const shareUrl = 'https://www.declicbelgium.be';
  const shareText = language === 'fr' 
    ? `J'ai découvert mon profil de carrière avec Ikigai Déclic ! Mon top métier : ${result?.top_career || 'en cours de découverte'}. Fais le test toi aussi !`
    : `Ik heb mijn carrièreprofiel ontdekt met Ikigai Déclic! Mijn top beroep: ${result?.top_career || 'nog te ontdekken'}. Doe de test ook!`;

  const handleShareLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleShareFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleShareTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  // Send results by email (actual execution)
  const executeSendEmail = async () => {
    if (!email) return;
    
    setSendingEmail(true);
    setEmailError('');
    
    try {
      // Build careers array in the format expected by the backend
      const careersPayload = (result?.ranked_careers || []).slice(0, 3).map(c => ({
        name: c.name || '',
        description: c.explanation || '',
        score: c.score || 0
      }));
      
      // Build scores from Stage 1 original result or from ranked careers
      const scoresPayload = originalResult?.scores || {
        passion: 7, mission: 6, profession: 7, vocation: 6
      };
      
      await axios.post(`${API}/send-results`, {
        email,
        scores: scoresPayload,
        careers: careersPayload,
        language,
        include_pdf: true
      });
      
      setEmailSent(true);
    } catch (error) {
      console.error('Error sending email:', error);
      const detail = error.response?.data?.detail || '';
      setEmailError(
        language === 'fr' 
          ? `Erreur lors de l'envoi. ${detail}` 
          : `Fout bij verzenden. ${detail}`
      );
    } finally {
      setSendingEmail(false);
    }
  };

  // Wrapper for email with feedback check
  const handleSendEmail = () => {
    if (!email) return;
    handleActionWithFeedback('email');
  };

  // Submit post-test feedback
  const submitFeedback = async () => {
    if (sessionId) {
      try {
        // Update analytics session
        await axios.patch(`${API}/analytics/session/${sessionId}`, {
          stage2_completed: true,
          post_test_answer: postTestAnswer.join(','), // Join multiple answers with comma
          engagement_answer: engagementAnswer.join(',')
        });

        // Update complete user results with Stage 2 data
        await axios.patch(`${API}/user-results/${sessionId}`, {
          stage2_selected_careers: selectedCareers,
          stage2_ranked_careers: ranked_careers?.map((c, idx) => ({
            name: c.name,
            score: c.score,
            rank: idx + 1
          })),
          stage2_top_career: top_career,
          post_test_answer: postTestAnswer.join(','),
          engagement_answer: engagementAnswer.join(',')
        });
        console.log('Stage 2 results saved to user_results');
      } catch (error) {
        console.error('Error submitting feedback:', error);
      }
    }
    setFeedbackSubmitted(true);
    setShowPostTest(false);
  };
  
  if (!result) {
    return (
      <div className="min-h-screen bg-[#EBEBEB] flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-semibold text-[#1D1D1D] mb-4">No results found</p>
          <button
            onClick={() => navigate('/')}
            className="neo-button-primary"
          >
            {t('backHome')}
          </button>
        </div>
      </div>
    );
  }
  
  const { ranked_careers, top_career, explanation } = result;
  
  // Get top career details from originalResult
  const getTopCareerDetails = () => {
    if (!originalResult?.careers) return null;
    return originalResult.careers.find(c => c.name === top_career);
  };
  
  const topCareerDetails = getTopCareerDetails();
  
  // Extract top domains from original result (Stage 1)
  const getTopDomains = () => {
    if (!originalResult?.scores) return [];
    const scores = originalResult.scores;
    const sortedDomains = Object.entries(scores)
      .sort((a, b) => b[1] - a[1])
      .filter(([_, score]) => score >= 50)
      .slice(0, 3)
      .map(([domain, score]) => [domain, Math.min(score, 100)]); // Cap at 100%
    return sortedDomains;
  };
  
  const topDomains = getTopDomains();
  
  // Icons for ranking positions
  const rankIcons = {
    1: <Trophy className="w-8 h-8 text-yellow-500" />,
    2: <Medal className="w-7 h-7 text-gray-400" />,
    3: <Award className="w-6 h-6 text-amber-600" />
  };
  
  // Colors for ranking positions
  const rankColors = {
    1: { bg: 'bg-gradient-to-br from-yellow-50 to-amber-100', border: 'border-yellow-400', text: 'text-yellow-600' },
    2: { bg: 'bg-gradient-to-br from-gray-50 to-gray-100', border: 'border-gray-300', text: 'text-gray-500' },
    3: { bg: 'bg-gradient-to-br from-amber-50 to-orange-100', border: 'border-amber-400', text: 'text-amber-600' }
  };

  // Domain colors for badges
  const domainColors = {
    social: '#E91E63',
    education: '#9C27B0',
    health: '#F44336',
    creativity: '#FF9800',
    technology: '#2196F3',
    technical: '#607D8B',
    science: '#4CAF50',
    business: '#673AB7',
    administration: '#795548',
    sports: '#00BCD4'
  };

  // Post-test question modal
  if (showPostTest && !feedbackSubmitted) {
    const postTestSelectionText = language === 'fr' 
      ? `Tu peux choisir jusqu'à 2 réponses (${postTestAnswer.length}/2)`
      : `Je kunt tot 2 antwoorden kiezen (${postTestAnswer.length}/2)`;
    
    const engagementSelectionText = language === 'fr'
      ? `Tu peux choisir jusqu'à 2 réponses (${engagementAnswer.length}/2)`
      : `Je kunt tot 2 antwoorden kiezen (${engagementAnswer.length}/2)`;

    return (
      <div className="min-h-screen bg-[#EBEBEB] flex flex-col">
        <Header />
        <main className="flex-1 px-4 md:px-8 py-8 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-xl w-full bg-white rounded-2xl shadow-xl border border-[#E0E0E0] overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#A4187A] to-[#8E24AA] p-6 text-white text-center">
              <MessageCircle className="w-12 h-12 mx-auto mb-3" />
              <h2 className="text-xl font-bold">{t('stage2Results.postTestTitle')}</h2>
            </div>

            <div className="p-6 space-y-6">
              {/* Post-test question */}
              <div>
                <p className="font-semibold text-[#1D1D1D] mb-2">{t('stage2Results.postTestQuestion')}</p>
                <p className="text-sm text-[#A4187A] mb-4">{postTestSelectionText}</p>
                <div className="space-y-2">
                  {['discovered_new', 'confirmed_idea', 'new_directions', 'still_reflecting'].map((value) => {
                    const isSelected = postTestAnswer.includes(value);
                    const isDisabled = !isSelected && postTestAnswer.length >= 2;
                    
                    return (
                      <button
                        key={value}
                        onClick={() => togglePostTestAnswer(value)}
                        disabled={isDisabled}
                        className={`w-full p-3 rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
                          isSelected
                            ? 'border-[#A4187A] bg-[#F3E5F5]'
                            : isDisabled
                              ? 'border-[#E0E0E0] opacity-50 cursor-not-allowed'
                              : 'border-[#E0E0E0] hover:border-[#A4187A]/50'
                        }`}
                      >
                        {/* Checkbox indicator */}
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-[#A4187A] border-[#A4187A]' : 'border-[#E0E0E0]'
                        }`}>
                          {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <span className={isSelected ? 'text-[#A4187A] font-medium' : 'text-[#1D1D1D]'}>
                          {t(`stage2Results.postTestAnswers.${value}`)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Engagement question */}
              <div className="pt-4 border-t border-[#E0E0E0]">
                <p className="font-semibold text-[#1D1D1D] mb-2">{t('stage2Results.engagementTitle')}</p>
                <p className="text-sm text-[#FB6823] mb-4">{engagementSelectionText}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {['meet_professional', 'explore_training', 'discuss_advisor', 'continue_exploring'].map((value) => {
                    const isSelected = engagementAnswer.includes(value);
                    const isDisabled = !isSelected && engagementAnswer.length >= 2;
                    
                    return (
                      <button
                        key={value}
                        onClick={() => toggleEngagementAnswer(value)}
                        disabled={isDisabled}
                        className={`p-3 rounded-xl border-2 transition-all text-sm flex items-center gap-2 ${
                          isSelected
                            ? 'border-[#FB6823] bg-[#FFF3E0]'
                            : isDisabled
                              ? 'border-[#E0E0E0] opacity-50 cursor-not-allowed'
                              : 'border-[#E0E0E0] hover:border-[#FB6823]/50'
                        }`}
                      >
                        {/* Checkbox indicator */}
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-[#FB6823] border-[#FB6823]' : 'border-[#E0E0E0]'
                        }`}>
                          {isSelected && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <span className={isSelected ? 'text-[#FB6823] font-medium' : 'text-[#1D1D1D]'}>
                          {t(`stage2Results.engagementAnswers.${value}`)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowPostTest(false)}
                  className="flex-1 py-3 border-2 border-[#E0E0E0] rounded-xl text-[#1D1D1D]/60 hover:bg-[#F5F5F5] transition-colors"
                >
                  {t('stage2Results.skipQuestion')}
                </button>
                <button
                  onClick={submitFeedback}
                  disabled={postTestAnswer.length === 0}
                  className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
                    postTestAnswer.length > 0
                      ? 'bg-gradient-to-r from-[#A4187A] to-[#8E24AA] text-white hover:shadow-lg'
                      : 'bg-[#E0E0E0] text-[#1D1D1D]/40 cursor-not-allowed'
                  }`}
                >
                  {t('stage2Results.submitFeedback')}
                </button>
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }

  // Feedback modal that appears before download/email
  if (showFeedbackModal) {
    const feedbackSelectionText = language === 'fr' 
      ? `Tu peux choisir jusqu'à 2 réponses (${postTestAnswer.length}/2)`
      : `Je kunt tot 2 antwoorden kiezen (${postTestAnswer.length}/2)`;
    
    const engagementSelectionText = language === 'fr'
      ? `Tu peux choisir jusqu'à 2 réponses (${engagementAnswer.length}/2)`
      : `Je kunt tot 2 antwoorden kiezen (${engagementAnswer.length}/2)`;

    return (
      <div className="min-h-screen bg-[#EBEBEB] flex flex-col">
        <Header />
        <main className="flex-1 px-4 md:px-8 py-8 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-xl w-full bg-white rounded-2xl shadow-xl border border-[#E0E0E0] overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#FB6823] to-[#A4187A] p-6 text-white text-center">
              <div className="flex items-center justify-center gap-3 mb-3">
                {pendingAction === 'pdf' && <FileText className="w-10 h-10" />}
                {pendingAction === 'image' && <Image className="w-10 h-10" />}
                {pendingAction === 'email' && <Mail className="w-10 h-10" />}
              </div>
              <h2 className="text-xl font-bold">
                {language === 'fr' ? '🎁 Débloque tes résultats !' : '🎁 Ontgrendel je resultaten!'}
              </h2>
              <p className="text-white/90 mt-2 text-sm">
                {language === 'fr' 
                  ? 'Aide-nous à améliorer Déclic en répondant à 2 questions rapides (30 secondes)'
                  : 'Help ons Déclic te verbeteren door 2 snelle vragen te beantwoorden (30 seconden)'}
              </p>
            </div>

            <div className="p-6 space-y-6">
              {/* Question 1: What did you discover? */}
              <div>
                <p className="font-semibold text-[#1D1D1D] mb-2">{t('stage2Results.postTestQuestion')}</p>
                <p className="text-sm text-[#A4187A] mb-4">{feedbackSelectionText}</p>
                <div className="space-y-2">
                  {['discovered_new', 'confirmed_idea', 'new_directions', 'still_reflecting'].map((value) => {
                    const isSelected = postTestAnswer.includes(value);
                    const isDisabled = !isSelected && postTestAnswer.length >= 2;
                    
                    return (
                      <button
                        key={value}
                        onClick={() => togglePostTestAnswer(value)}
                        disabled={isDisabled}
                        className={`w-full p-3 rounded-xl border-2 transition-all text-left flex items-center gap-3 ${
                          isSelected
                            ? 'border-[#A4187A] bg-[#F3E5F5]'
                            : isDisabled
                              ? 'border-[#E0E0E0] opacity-50 cursor-not-allowed'
                              : 'border-[#E0E0E0] hover:border-[#A4187A]/50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-[#A4187A] border-[#A4187A]' : 'border-[#E0E0E0]'
                        }`}>
                          {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                        </div>
                        <span className={isSelected ? 'text-[#A4187A] font-medium' : 'text-[#1D1D1D]'}>
                          {t(`stage2Results.postTestAnswers.${value}`)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Question 2: What do you want to do next? */}
              <div className="pt-4 border-t border-[#E0E0E0]">
                <p className="font-semibold text-[#1D1D1D] mb-2">{t('stage2Results.engagementTitle')}</p>
                <p className="text-sm text-[#FB6823] mb-4">{engagementSelectionText}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {['meet_professional', 'explore_training', 'discuss_advisor', 'continue_exploring'].map((value) => {
                    const isSelected = engagementAnswer.includes(value);
                    const isDisabled = !isSelected && engagementAnswer.length >= 2;
                    
                    return (
                      <button
                        key={value}
                        onClick={() => toggleEngagementAnswer(value)}
                        disabled={isDisabled}
                        className={`p-3 rounded-xl border-2 transition-all text-left flex items-center gap-2 ${
                          isSelected
                            ? 'border-[#FB6823] bg-[#FFF3E0]'
                            : isDisabled
                              ? 'border-[#E0E0E0] opacity-50 cursor-not-allowed'
                              : 'border-[#E0E0E0] hover:border-[#FB6823]/50'
                        }`}
                      >
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'bg-[#FB6823] border-[#FB6823]' : 'border-[#E0E0E0]'
                        }`}>
                          {isSelected && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                        </div>
                        <span className={`text-sm ${isSelected ? 'text-[#FB6823] font-medium' : 'text-[#1D1D1D]'}`}>
                          {t(`stage2Results.engagementAnswers.${value}`)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Submit buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowFeedbackModal(false);
                    setPendingAction(null);
                  }}
                  className="flex-1 py-3 border-2 border-[#E0E0E0] rounded-xl text-[#1D1D1D]/60 hover:bg-[#F5F5F5] transition-colors"
                >
                  {language === 'fr' ? 'Annuler' : 'Annuleren'}
                </button>
                <button
                  onClick={handleFeedbackSubmitAndAction}
                  disabled={postTestAnswer.length === 0}
                  className={`flex-1 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                    postTestAnswer.length > 0
                      ? 'bg-gradient-to-r from-[#FB6823] to-[#A4187A] text-white hover:shadow-lg'
                      : 'bg-[#E0E0E0] text-[#1D1D1D]/40 cursor-not-allowed'
                  }`}
                >
                  {pendingAction === 'pdf' && <FileText className="w-5 h-5" />}
                  {pendingAction === 'image' && <Image className="w-5 h-5" />}
                  {pendingAction === 'email' && <Mail className="w-5 h-5" />}
                  {language === 'fr' ? 'Valider et télécharger' : 'Valideren en downloaden'}
                </button>
              </div>
            </div>
          </motion.div>
        </main>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#EBEBEB] flex flex-col">
      <Header />
      
      {/* Confetti celebration */}
      <AnimatePresence>
        {showConfetti && <Confetti />}
      </AnimatePresence>
      
      <main className="flex-1 px-4 md:px-8 py-8">
        <div className="max-w-3xl mx-auto">
          
          {/* ============================================ */}
          {/* SECTION 1: PROFILE HEADER - Déclic Branding */}
          {/* ============================================ */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", duration: 0.8 }}
            className="text-center mb-8"
          >
            {/* Success Badge */}
            <motion.div 
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#A4187A] to-[#FB6823] rounded-full mb-4 shadow-lg"
            >
              <PartyPopper className="w-5 h-5 text-white" />
              <span className="text-sm font-bold text-white">Ikigai Déclic – Ton profil</span>
              <Rocket className="w-5 h-5 text-white" />
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-3xl md:text-4xl font-black text-[#A4187A] mb-2"
              data-testid="stage2-results-title"
            >
              {t('stage2Results.profileTitle')}
            </motion.h1>
            
            {/* Animated stars */}
            <motion.div 
              className="flex items-center justify-center gap-2 mt-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <motion.div
                animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
              >
                <Star className="w-6 h-6 text-[#FB6823] fill-[#FB6823]" />
              </motion.div>
              <motion.div
                animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.3, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 0.5 }}
              >
                <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
              </motion.div>
              <motion.div
                animate={{ rotate: [0, 15, -15, 0], scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 1.5 }}
              >
                <Star className="w-6 h-6 text-[#FB6823] fill-[#FB6823]" />
              </motion.div>
            </motion.div>
          </motion.div>
          
          {/* ============================================ */}
          {/* SECTION 2: TOP DOMAINS FROM STAGE 1 */}
          {/* ============================================ */}
          {topDomains.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="neo-card mb-6"
              data-testid="top-domains-section"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#A4187A] to-[#8E24AA] flex items-center justify-center">
                  <Compass className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-lg font-bold text-[#1D1D1D]">
                  {t('stage2Results.topDomains')}
                </h2>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {topDomains.map(([domain, score], idx) => (
                  <motion.div
                    key={domain}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.2 + idx * 0.1 }}
                    className="flex items-center gap-2 px-4 py-2 rounded-full"
                    style={{ 
                      backgroundColor: `${domainColors[domain] || '#A4187A'}15`,
                      border: `2px solid ${domainColors[domain] || '#A4187A'}40`
                    }}
                  >
                    <TrendingUp 
                      className="w-4 h-4" 
                      style={{ color: domainColors[domain] || '#A4187A' }}
                    />
                    <span 
                      className="font-semibold text-sm"
                      style={{ color: domainColors[domain] || '#A4187A' }}
                    >
                      {t(`domains.${domain}`)}
                    </span>
                    <span 
                      className="text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{ 
                        backgroundColor: domainColors[domain] || '#A4187A',
                        color: 'white'
                      }}
                    >
                      {score.toFixed(0)}%
                    </span>
                  </motion.div>
                ))}
              </div>
              
              <p className="text-sm text-[#1D1D1D]/60 mt-4">
                {t('stage2Results.basedOnAnswers')}
              </p>
            </motion.div>
          )}
          
          {/* ============================================ */}
          {/* SECTION 3: CAREER RANKING */}
          {/* ============================================ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#FB6823] to-[#F57C00] flex items-center justify-center">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-lg font-bold text-[#1D1D1D]">
                {t('stage2Results.careerRanking')}
              </h2>
            </div>
            
            <div className="space-y-4">
              {ranked_careers.map((career, index) => {
                const rank = career.rank;
                const colors = rankColors[rank] || rankColors[3];
                const isTopMatch = rank === 1;
                
                return (
                  <motion.div
                    key={career.name}
                    initial={{ opacity: 0, x: -50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.15 }}
                    className={`relative overflow-hidden rounded-2xl border-2 ${colors.border} ${colors.bg} p-5 ${
                      isTopMatch ? 'shadow-lg ring-2 ring-[#A4187A]/20' : ''
                    }`}
                    data-testid={`stage2-rank-${rank}`}
                  >
                    {/* Top Match Badge */}
                    {isTopMatch && (
                      <div className="absolute top-0 right-0 bg-[#A4187A] text-white px-4 py-1 rounded-bl-xl text-sm font-bold flex items-center gap-1">
                        <Star className="w-4 h-4 fill-white" />
                        {t('stage2.topMatch')}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4">
                      {/* Rank Icon */}
                      <div className={`flex-shrink-0 w-16 h-16 rounded-xl ${
                        isTopMatch ? 'bg-gradient-to-br from-yellow-100 to-amber-200' : 'bg-white/50'
                      } flex items-center justify-center shadow-sm`}>
                        {rankIcons[rank] || (
                          <span className="text-2xl font-black text-[#1D1D1D]/30">#{rank}</span>
                        )}
                      </div>
                      
                      {/* Career Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className={`text-xl md:text-2xl font-black ${
                          isTopMatch ? 'text-[#A4187A]' : 'text-[#1D1D1D]'
                        }`}>
                          {career.name}
                        </h3>
                        <div className="flex items-center gap-3 mt-2">
                          <div className="h-3 flex-1 max-w-[200px] rounded-full bg-white overflow-hidden shadow-inner">
                            <motion.div
                              className={`h-full rounded-full ${isTopMatch ? 'bg-gradient-to-r from-[#A4187A] to-[#FB6823]' : 'bg-[#FB6823]'}`}
                              initial={{ width: 0 }}
                              animate={{ width: `${Math.min(career.score, 100)}%` }}
                              transition={{ delay: 0.4 + index * 0.15, duration: 0.6 }}
                            />
                          </div>
                          <span className={`text-xl font-black ${isTopMatch ? 'text-[#A4187A]' : colors.text}`}>
                            {Math.min(career.score, 100).toFixed(0)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
          
          {/* ============================================ */}
          {/* SECTION 4: WHY THIS CAREER - Explanation */}
          {/* ============================================ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="neo-card mb-6 border-l-4 border-l-[#A4187A]"
            data-testid="stage2-explanation"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#A4187A] to-[#FB6823] flex items-center justify-center flex-shrink-0 shadow-lg">
                <Lightbulb className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#A4187A] mb-2">
                  {t('stage2Results.whyTopCareer')}
                </h3>
                <p className="text-[#1D1D1D]/80 leading-relaxed text-base">
                  {explanation}
                </p>
              </div>
            </div>
          </motion.div>

          {/* ============================================ */}
          {/* SECTION 5: CAREER DISCOVERY - Details */}
          {/* ============================================ */}
          {topCareerDetails && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55 }}
              className="neo-card mb-6"
              data-testid="career-discovery-section"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4CAF50] to-[#66BB6A] flex items-center justify-center">
                  <Search className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-lg font-bold text-[#1D1D1D]">
                  {t('stage2Results.careerDiscovery')} : {top_career}
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Typical Tasks */}
                {topCareerDetails.typical_day && (
                  <div className="p-4 bg-[#E8F5E9] rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Briefcase className="w-5 h-5 text-[#4CAF50]" />
                      <span className="font-semibold text-[#2E7D32]">{t('stage2Results.typicalTasks')}</span>
                    </div>
                    <p className="text-sm text-[#1D1D1D]/70">{topCareerDetails.typical_day}</p>
                  </div>
                )}

                {/* Skills */}
                {topCareerDetails.skills && topCareerDetails.skills.length > 0 && (
                  <div className="p-4 bg-[#E3F2FD] rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <UserCheck className="w-5 h-5 text-[#2196F3]" />
                      <span className="font-semibold text-[#1565C0]">{t('stage2Results.usefulSkills')}</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {topCareerDetails.skills.slice(0, 5).map((skill, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-white rounded-full text-[#1565C0]">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Studies */}
                {topCareerDetails.studies && (
                  <div className="p-4 bg-[#F3E5F5] rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <GraduationCap className="w-5 h-5 text-[#A4187A]" />
                      <span className="font-semibold text-[#A4187A]">{t('stage2Results.studiesInBelgium')}</span>
                    </div>
                    <p className="text-sm text-[#1D1D1D]/70">{topCareerDetails.studies}</p>
                  </div>
                )}

                {/* Salary */}
                {topCareerDetails.salary_range && (
                  <div className="p-4 bg-[#FFF3E0] rounded-xl">
                    <div className="flex items-center gap-2 mb-2">
                      <Euro className="w-5 h-5 text-[#FB6823]" />
                      <span className="font-semibold text-[#E65100]">{t('stage2Results.salaryRange')}</span>
                    </div>
                    <p className="text-sm text-[#1D1D1D]/70">{topCareerDetails.salary_range}</p>
                  </div>
                )}
              </div>
              
              {/* Formations Section - Where to Study */}
              <div className="mt-4 pt-4 border-t-2 border-dashed border-[#E0E0E0]">
                <div className="flex items-center gap-2 mb-3">
                  <GraduationCap className="w-5 h-5 text-[#A4187A]" />
                  <span className="font-bold text-[#1D1D1D]">
                    {language === 'nl' ? 'Waar studeren?' : 'Où se former ?'}
                  </span>
                </div>
                <CareerFormations 
                  careerName={top_career} 
                  language={language}
                  autoExpand={true}
                />
              </div>
            </motion.div>
          )}

          {/* ============================================ */}
          {/* SECTION 6: LABOUR MARKET DATA (PART 7) */}
          {/* ============================================ */}
          {careerMarketData && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className={`neo-card mb-6 ${careerMarketData.is_shortage ? 'border-l-4 border-l-[#4CAF50]' : ''}`}
              data-testid="labour-market-section"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#2196F3] to-[#42A5F5] flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-lg font-bold text-[#1D1D1D]">
                  {t('stage2Results.labourMarket')}
                </h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Market Demand */}
                <div className="text-center p-3 bg-[#E3F2FD] rounded-xl">
                  <p className="text-xs text-[#1D1D1D]/60 mb-1">{t('stage2Results.marketDemand')}</p>
                  <p className={`font-bold ${
                    careerMarketData.demand_level === 'very_high' ? 'text-[#4CAF50]' :
                    careerMarketData.demand_level === 'high' ? 'text-[#8BC34A]' :
                    'text-[#FF9800]'
                  }`}>
                    {careerMarketData.demand_label}
                  </p>
                </div>

                {/* Shortage */}
                {careerMarketData.is_shortage && (
                  <div className="text-center p-3 bg-[#E8F5E9] rounded-xl">
                    <p className="text-xs text-[#1D1D1D]/60 mb-1">{t('stage2Results.shortage')}</p>
                    <div className="flex items-center justify-center gap-1">
                      <CheckCircle className="w-4 h-4 text-[#4CAF50]" />
                      <span className="font-bold text-[#4CAF50]">Oui</span>
                    </div>
                  </div>
                )}

                {/* Trend */}
                <div className="text-center p-3 bg-[#FFF3E0] rounded-xl">
                  <p className="text-xs text-[#1D1D1D]/60 mb-1">{t('stage2Results.trend')}</p>
                  <p className="font-bold text-[#FB6823]">{careerMarketData.growth_label}</p>
                </div>

                {/* Regions */}
                {careerMarketData.regions_labels && careerMarketData.regions_labels.length > 0 && (
                  <div className="text-center p-3 bg-[#F3E5F5] rounded-xl">
                    <p className="text-xs text-[#1D1D1D]/60 mb-1">{t('stage2Results.concernedRegions')}</p>
                    <p className="font-bold text-[#A4187A] text-sm">
                      {careerMarketData.regions_labels.join(', ')}
                    </p>
                  </div>
                )}
              </div>

              <p className="text-xs text-[#1D1D1D]/50 mt-3">
                {t('stage2Results.source')}: {careerMarketData.source}
              </p>
            </motion.div>
          )}
          
          {/* ============================================ */}
          {/* SECTION 7: ENCOURAGE REAL-WORLD EXPLORATION */}
          {/* ============================================ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65 }}
            className="bg-gradient-to-br from-[#FFF3E0] to-[#FFE0B2] rounded-2xl p-6 mb-6 border border-[#FB6823]/20"
            data-testid="explore-section"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#FB6823] flex items-center justify-center flex-shrink-0">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#FB6823] mb-2">
                  {t('stage2Results.exploreInRealLife')}
                </h3>
                <p className="text-[#1D1D1D]/70 mb-3">
                  {t('stage2Results.exploreMessage')}
                </p>
                <p className="text-[#1D1D1D]/80 font-medium">
                  {t('stage2Results.exploreAdvice')}
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* ============================================ */}
          {/* SECTION 8: DÉCLIC EVENTS */}
          {/* ============================================ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-gradient-to-br from-[#F3E5F5] to-[#E1BEE7] rounded-2xl p-6 mb-8 border border-[#A4187A]/20"
            data-testid="declic-events-section"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#A4187A] flex items-center justify-center flex-shrink-0">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-[#A4187A] mb-2">
                  {t('stage2Results.declicEvents')}
                </h3>
                <p className="text-[#1D1D1D]/70 mb-4">
                  {t('stage2Results.declicEventsMessage')}
                </p>
                <a
                  href="https://www.declicbelgium.be"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#A4187A] text-white rounded-full font-semibold hover:bg-[#5a0b91] transition-colors shadow-md"
                >
                  <Users className="w-5 h-5" />
                  {t('stage2Results.discoverActivities')}
                  <ChevronRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </motion.div>
          
          {/* ============================================ */}
          {/* SECTION 9: FEEDBACK - DONNE-NOUS TON AVIS */}
          {/* ============================================ */}
          {!feedbackSubmitted && sessionId && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75 }}
              className="bg-gradient-to-r from-[#A4187A] to-[#FB6823] rounded-2xl p-6 mb-8 shadow-lg"
              data-testid="feedback-section"
            >
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-3xl">🎁</span>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-xl font-bold text-white mb-2">
                    {language === 'fr' ? "Débloque tes téléchargements !" : "Ontgrendel je downloads!"}
                  </h3>
                  <p className="text-white/90 mb-4">
                    {language === 'fr' 
                      ? "Donne-nous ton avis (30 sec) pour télécharger tes résultats en PDF, image ou les recevoir par email."
                      : "Geef ons je mening (30 sec) om je resultaten te downloaden als PDF, afbeelding of per e-mail te ontvangen."}
                  </p>
                  <button
                    onClick={() => setShowPostTest(true)}
                    className="px-6 py-3 bg-white text-[#A4187A] rounded-xl font-bold hover:bg-white/90 transition-all shadow-md flex items-center gap-2 mx-auto md:mx-0"
                    data-testid="feedback-btn-prominent"
                  >
                    <MessageCircle className="w-5 h-5" />
                    {language === 'fr' ? "Donner mon avis" : "Mijn mening geven"}
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Feedback success message */}
          {feedbackSubmitted && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="mb-6 bg-green-50 border-2 border-green-500 rounded-2xl p-4 flex items-center gap-3"
            >
              <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
              <div>
                <p className="font-bold text-green-700">
                  {language === 'fr' ? "Merci pour ton avis ! 🎉" : "Bedankt voor je mening! 🎉"}
                </p>
                <p className="text-green-600 text-sm">
                  {language === 'fr' 
                    ? "Tes téléchargements sont maintenant débloqués." 
                    : "Je downloads zijn nu ontgrendeld."}
                </p>
              </div>
            </motion.div>
          )}
          
          {/* ============================================ */}
          {/* ACTION BUTTONS */}
          {/* ============================================ */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="space-y-4"
          >
            {/* ===== DOWNLOAD & SHARE SECTION ===== */}
            <div className="bg-white rounded-2xl p-5 border border-[#E0E0E0] shadow-sm space-y-4">
              
              {/* Download section */}
              <div>
                <h3 className="font-bold text-[#1D1D1D] mb-3 flex items-center gap-2">
                  <Download className="w-5 h-5 text-[#A4187A]" />
                  {language === 'fr' ? 'Télécharger mes résultats' : 'Mijn resultaten downloaden'}
                  {!feedbackSubmitted && (
                    <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                      {language === 'fr' ? '🔒 Avis requis' : '🔒 Mening vereist'}
                    </span>
                  )}
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                    className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${
                      feedbackSubmitted
                        ? 'bg-gradient-to-r from-[#A4187A] to-[#8E24AA] text-white hover:shadow-lg'
                        : 'bg-gradient-to-r from-[#A4187A]/70 to-[#8E24AA]/70 text-white/90'
                    }`}
                    data-testid="download-pdf-btn"
                  >
                    {downloadingPdf ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <FileText className="w-5 h-5" />
                    )}
                    PDF
                  </button>
                  <button
                    onClick={handleDownloadImage}
                    disabled={downloadingImage}
                    className={`flex-1 py-3 px-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 ${
                      feedbackSubmitted
                        ? 'bg-gradient-to-r from-[#FB6823] to-[#F57C00] text-white hover:shadow-lg'
                        : 'bg-gradient-to-r from-[#FB6823]/70 to-[#F57C00]/70 text-white/90'
                    }`}
                    data-testid="download-image-btn"
                  >
                    {downloadingImage ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Image className="w-5 h-5" />
                    )}
                    Image
                  </button>
                </div>
              </div>
              
              {/* Email section */}
              <div>
                <h3 className="font-bold text-[#1D1D1D] mb-3 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#4CAF50]" />
                  {language === 'fr' ? 'Recevoir mes résultats par email' : 'Mijn resultaten per e-mail ontvangen'}
                  {!feedbackSubmitted && (
                    <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">
                      {language === 'fr' ? '🔒 Avis requis' : '🔒 Mening vereist'}
                    </span>
                  )}
                </h3>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    placeholder={language === 'fr' ? 'Ton adresse email...' : 'Je e-mailadres...'}
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError('');
                      setEmailSent(false);
                    }}
                    disabled={emailSent}
                    className="flex-1 px-4 py-3 border-2 border-[#E0E0E0] rounded-xl focus:border-[#A4187A] focus:outline-none transition-colors disabled:bg-gray-100"
                    data-testid="email-input-stage2"
                  />
                  <button
                    onClick={handleSendEmail}
                    disabled={!email || sendingEmail || emailSent}
                    className={`py-3 px-6 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                      emailSent 
                        ? 'bg-green-500 text-white'
                        : feedbackSubmitted
                          ? 'bg-gradient-to-r from-[#4CAF50] to-[#43A047] text-white hover:shadow-lg disabled:opacity-50'
                          : 'bg-gradient-to-r from-[#4CAF50]/70 to-[#43A047]/70 text-white/90 disabled:opacity-50'
                    }`}
                    data-testid="send-email-btn"
                  >
                    {sendingEmail ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : emailSent ? (
                      <Check className="w-5 h-5" />
                    ) : (
                      <Mail className="w-5 h-5" />
                    )}
                    {sendingEmail 
                      ? (language === 'fr' ? 'Envoi...' : 'Verzenden...')
                      : emailSent 
                        ? (language === 'fr' ? 'Envoyé !' : 'Verzonden!')
                        : (language === 'fr' ? 'Envoyer' : 'Verzenden')}
                  </button>
                </div>
                {emailError && (
                  <p className="text-red-500 text-sm mt-2">{emailError}</p>
                )}
                {emailSent && (
                  <p className="text-green-600 text-sm mt-2">
                    {language === 'fr' 
                      ? '✓ Tes résultats ont été envoyés à ton adresse email !' 
                      : '✓ Je resultaten zijn naar je e-mailadres verzonden!'}
                  </p>
                )}
              </div>
              
              {/* Social sharing section */}
              <div>
                <h3 className="font-bold text-[#1D1D1D] mb-3 flex items-center gap-2">
                  <Share2 className="w-5 h-5 text-[#FB6823]" />
                  {language === 'fr' ? 'Partager sur les réseaux' : 'Delen op sociale media'}
                </h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={handleShareLinkedIn}
                    className="flex-1 min-w-[100px] py-2.5 px-4 bg-[#0077B5] text-white rounded-xl font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    data-testid="share-linkedin-btn"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                  </button>
                  <button
                    onClick={handleShareFacebook}
                    className="flex-1 min-w-[100px] py-2.5 px-4 bg-[#1877F2] text-white rounded-xl font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    data-testid="share-facebook-btn"
                  >
                    <Facebook className="w-4 h-4" />
                    Facebook
                  </button>
                  <button
                    onClick={handleShareTwitter}
                    className="flex-1 min-w-[100px] py-2.5 px-4 bg-[#1DA1F2] text-white rounded-xl font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                    data-testid="share-twitter-btn"
                  >
                    <Twitter className="w-4 h-4" />
                    X
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className={`flex-1 min-w-[100px] py-2.5 px-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 ${
                      linkCopied 
                        ? 'bg-green-500 text-white' 
                        : 'bg-[#E0E0E0] text-[#1D1D1D] hover:bg-[#D0D0D0]'
                    }`}
                    data-testid="copy-link-btn"
                  >
                    {linkCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {linkCopied 
                      ? (language === 'fr' ? 'Copié !' : 'Gekopieerd!') 
                      : (language === 'fr' ? 'Copier lien' : 'Link kopiëren')}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/results', { state: { result: originalResult } })}
                className="neo-button-outline flex items-center justify-center gap-2 flex-1"
                data-testid="stage2-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                {t('stage2.backToResults')}
              </button>
              <button
                onClick={() => navigate('/stage2', { 
                  state: { 
                    selectedCareers,
                    originalResult 
                  } 
                })}
                className="neo-button-outline flex items-center justify-center gap-2 flex-1"
                data-testid="stage2-retake-btn"
              >
                <RefreshCw className="w-5 h-5" />
                {t('stage2.retakeStage2')}
              </button>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/questionnaire')}
                className="neo-button-secondary flex items-center justify-center gap-2 flex-1"
                data-testid="retake-full-btn"
              >
                <RefreshCw className="w-5 h-5" />
                {t('retakeTest')}
              </button>
              <button
                onClick={() => navigate('/')}
                className="neo-button-outline flex items-center justify-center gap-2 flex-1"
                data-testid="home-btn"
              >
                <Home className="w-5 h-5" />
                {t('backHome')}
              </button>
            </div>
            
            {/* Back to Déclic Website - Primary CTA */}
            <a
              href="https://www.declicbelgium.be"
              target="_blank"
              rel="noopener noreferrer"
              className="neo-button-primary flex items-center justify-center gap-2 w-full"
              data-testid="declic-website-btn"
            >
              <ExternalLink className="w-5 h-5" />
              {t('backToDeclic')}
            </a>
          </motion.div>
          
          {/* ============================================ */}
          {/* FOOTER BRANDING */}
          {/* ============================================ */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-10 pt-6 border-t border-[#E0E0E0] text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="text-2xl font-black text-[#A4187A]">Déclic</span>
              <span className="text-lg text-[#1D1D1D]/60">–</span>
              <span className="text-[#FB6823] font-semibold">{t('stage2Results.revealPotential')}</span>
            </div>
            <p className="text-sm text-[#1D1D1D]/50">
              {t('stage2Results.testMadeOn')}{' '}
              <a 
                href="https://www.declicbelgium.be" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#A4187A] hover:underline font-medium"
              >
                www.declicbelgium.be
              </a>
            </p>
          </motion.div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
